import {
  COMPONENT_ID_8_DIGIT_SIC,
  COMPONENT_ID_ADDRESS,
  COMPONENT_ID_ANNUAL_SALES,
  COMPONENT_ID_HEADCOUNT,
  COMPONENT_ID_ISIC_REV_4_CODE,
  COMPONENT_ID_NAICS_2022,
  ENTERPRISE_CERTIFICATIONS_PROPERTY,
} from 'constants/ontologyConstants';
import { PublicSupplier } from 'types/suppliers';

export enum KeywordAppearance {
  SHOULD = 'should',
  MUST = 'must',
  MUST_NOT = 'must_not',
}

enum TargetNetwork {
  ALL = 'all',
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}

export type EnterpriseAggregationRequest = {
  ontComponentFilters: {
    [ontPropertyComponentId: string]: EnterpriseSearchRequest;
  };
  keywordFilters: {
    query: string;
    appearance: KeywordAppearance;
  }[];
  enterpriseClassFilter?: string[];
  productCategoryFilters: string[];
  matchThreshold: number;
  targetNetwork: TargetNetwork;
};

export type BaseEnterpriseSearchRequest = {
  ontPropertyComponentId: string;
  shortName?: string;
};

export type EnterpriseSearchRequest =
  | EnterpriseSearchNumericRequest
  | EnterpriseSearchStringRequest
  | EnterpriseSearchLocationRequest
  | EnterpriseSearchEnumerationRequest;

export enum EnterpriseSearchFilterType {
  NUMERIC = 'NUMERIC',
  STRING = 'STRING',
  LOCATION = 'LOCATION',
  ENUMERATION = 'ENUMERATION',
}

export type EnterpriseSearchNumericalFilter = {
  min?: number;
  max?: number;
};

export type EnterpriseSearchValuesFilter = {
  values: string[];
};

export type EnterpriseSearchNumericRequest = BaseEnterpriseSearchRequest & {
  type: EnterpriseSearchFilterType.NUMERIC;
  filter?: EnterpriseSearchNumericalFilter;
};

export type EnterpriseSearchEnumerationRequest = BaseEnterpriseSearchRequest & {
  type: EnterpriseSearchFilterType.ENUMERATION;
  filter?: EnterpriseSearchValuesFilter;
};

export type EnterpriseSearchStringRequest = BaseEnterpriseSearchRequest & {
  type: EnterpriseSearchFilterType.STRING;
  filter?: EnterpriseSearchValuesFilter;
};

export type EnterpriseSearchLocationRequest = BaseEnterpriseSearchRequest & {
  type: EnterpriseSearchFilterType.LOCATION;
  filter?: EnterpriseSearchValuesFilter;
};

export type EnterpriseAggregationResponse = {
  count: number;
  normalizedBuckets: {
    score: number;
    enterpriseCount: number;
    inNetworkCount: number;
  }[];
  componentHistograms: {
    [componentId: string]: {
      label: string;
      terms?: TermBucket[];
      histogram?: VariableWidthHistogramBucket[];
      hexGrid?: TermBucket[];
    };
  };
  enterprises: {
    score?: number;
    id: string;
  }[];
  matchThreshold: number;
};

export type TermBucket = {
  key: string | number;
  enterpriseCount: number;
  inNetworkCount: number;
};

export type VariableWidthHistogramBucket = TermBucket & {
  min: number;
  max: number;
};

export type KeywordAutocompleteOption = {
  query: string;
  appearance: KeywordAppearance;
  active: boolean;
};

export type AutocompleteOption = {
  value: string;
  longName: string;
  shortName: string;
  active?: boolean;
  category?: string;
};

export const EMPTY_ENTERPRISE_AGGREGATION_REQUEST: EnterpriseAggregationRequest =
  {
    keywordFilters: [],
    ontComponentFilters: {
      [ENTERPRISE_CERTIFICATIONS_PROPERTY.CERTIFICATIONS_COMPONENT]: {
        ontPropertyComponentId:
          ENTERPRISE_CERTIFICATIONS_PROPERTY.CERTIFICATIONS_COMPONENT,
        type: EnterpriseSearchFilterType.ENUMERATION,
      },
      [COMPONENT_ID_ADDRESS]: {
        ontPropertyComponentId: COMPONENT_ID_ADDRESS,
        type: EnterpriseSearchFilterType.LOCATION,
      },
      [COMPONENT_ID_ANNUAL_SALES]: {
        ontPropertyComponentId: COMPONENT_ID_ANNUAL_SALES,
        type: EnterpriseSearchFilterType.NUMERIC,
      },
      [COMPONENT_ID_HEADCOUNT]: {
        ontPropertyComponentId: COMPONENT_ID_HEADCOUNT,
        type: EnterpriseSearchFilterType.NUMERIC,
      },
      [COMPONENT_ID_NAICS_2022]: {
        ontPropertyComponentId: COMPONENT_ID_NAICS_2022,
        type: EnterpriseSearchFilterType.ENUMERATION,
      },
      [COMPONENT_ID_8_DIGIT_SIC]: {
        ontPropertyComponentId: COMPONENT_ID_8_DIGIT_SIC,
        type: EnterpriseSearchFilterType.ENUMERATION,
      },
      [COMPONENT_ID_ISIC_REV_4_CODE]: {
        ontPropertyComponentId: COMPONENT_ID_ISIC_REV_4_CODE,
        type: EnterpriseSearchFilterType.ENUMERATION,
      },
    },
    productCategoryFilters: [],
    matchThreshold: 0.9,
    targetNetwork: TargetNetwork.ALL,
  };

export type SupplierSummaryBulkRequest = {
  enterpriseIds: string[];
};

export type SupplierSummaryData = {
  id: string;
  name?: string;
  location?: string;
  shortLocation?: string;
  latitude?: number;
  longitude?: number;
  headcount?: number;
  annualSales?: number;
  website?: string;
  businessListingShortUrl?: string;
  logoAttachmentId?: string;
  supplierClasses?: { ontId: string; display: string }[];
  error?: string;
};

export type SupplierSummaryBulkResponse = {
  error?: string;
  cardData?: SupplierSummaryData[];
  publicSuppliers?: Array<PublicSupplier & { orgSupplierId?: string }>;
};
